import { createContext, useCallback, useContext, useState } from "react";

export interface ScreenshotState {
  state: "idle" | "capturing" | "uploading" | "complete";
}

export type GlobalScreenshotReturnProps = {
  screenshotStates: Record<string, ScreenshotState>;
  updateScreenshotState: (screenId: string, state: Partial<ScreenshotState>) => void;
  updateScreenshotStates: (states: Record<string, ScreenshotState>) => void;
};

export const globalScreenshotContext = createContext<GlobalScreenshotReturnProps | null>(null);

const { Provider } = globalScreenshotContext;

export function GlobalScreenshotProvider({ children }: React.PropsWithChildren<{}>) {
  const [screenshotStates, setScreenshotStates] = useState<Record<string, ScreenshotState>>({});

  const updateScreenshotState = useCallback((screenId: string, state: Partial<ScreenshotState>) => {
    setScreenshotStates(prev => ({
      ...prev,
      [screenId]: {
        ...prev[screenId],
        ...state,
      },
    }));
  }, []);

  const updateScreenshotStates = useCallback((states: Record<string, ScreenshotState>) => {
    setScreenshotStates(prev => ({
      ...prev,
      ...states,
    }));
  }, []);

  return (
    <Provider
      value={{
        screenshotStates,
        updateScreenshotState,
        updateScreenshotStates,
      }}
    >
      {children}
    </Provider>
  );
}

export function useGlobalScreenshot(): GlobalScreenshotReturnProps {
  const context = useContext(globalScreenshotContext);
  if (!context) {
    throw new Error("useGlobalScreenshot must be used within a GlobalScreenshotProvider");
  }
  return context;
}

export interface LocalScreenshotReturnProps extends ScreenshotState {
  screenId: string;
}

export const LocalScreenshotContext = createContext<LocalScreenshotReturnProps | null>(null);

export function LocalScreenshotProvider({ children, screenId }: React.PropsWithChildren<{ screenId: string }>) {
  const { screenshotStates } = useGlobalScreenshot();

  const state = screenshotStates[screenId] ?? { state: "idle" };

  return (
    <LocalScreenshotContext.Provider
      value={{ ...state, screenId }}
    >
      {children}
    </LocalScreenshotContext.Provider>
  );
}

export function useLocalScreenshot(): LocalScreenshotReturnProps {
  const context = useContext(LocalScreenshotContext);
  if (!context) {
    throw new Error("useLocalScreenshot must be used within a LocalScreenshotProvider");
  }
  return context;
}